/** P-04 — address book: list + add/edit (modal) + set default + remove. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  addAddress,
  listAddresses,
  removeAddress,
  setDefaultAddress,
  updateAddress,
} from "../../api/patient";
import { describeError } from "../../api/errors";
import type { Address, AddressInput } from "../../api/patient";
import { useFormSubmit } from "../../api/useFormSubmit";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";
import { EditIcon, IconButton, RowActions, TrashIcon } from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { StateSelect } from "../../components/ui/StateSelect";
import { isValidPinCode, pinDigits } from "../../lib/pincode";

export function Addresses() {
  const qc = useQueryClient();
  const { data: addresses = [] } = useQuery({ queryKey: ["addresses"], queryFn: listAddresses });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["addresses"] });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  // the address whose removal is being confirmed (typing "delete")
  const [removing, setRemoving] = useState<Address | null>(null);
  const addKey = useIdempotencyKey();

  const failed = (e: unknown) => toast.error(describeError(e));
  const remove = useMutation({
    mutationFn: removeAddress,
    onSuccess: invalidate,
    onError: failed,
    onSettled: () => setRemoving(null),
  });
  const makeDefault = useMutation({ mutationFn: setDefaultAddress, onSuccess: invalidate, onError: failed });

  return (
    <div className="space-y-5">
      {/* On a phone the button drops under the title rather than squeezing into a two-line pill (RESP-19). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Addresses</h2>
          <p className="text-sm text-gray-500">Delivery addresses for your orders.</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="shrink-0 whitespace-nowrap rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90"
        >
          + Add address
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {addresses.map((a) => (
          <div key={a.id} className="rounded-xl border bg-white p-4 shadow-xs">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 font-medium">
                  {a.label || a.name}
                  {a.is_default && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Default</span>}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {a.line1}
                  {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state_code} {a.pin_code}
                </div>
                <div className="text-xs text-gray-400">{a.name} · {a.phone}</div>
              </div>
              <RowActions>
                <IconButton label="Edit" tone="brand" onClick={() => { setEditing(a); setFormOpen(true); }}>
                  <EditIcon />
                </IconButton>
                <IconButton label="Remove" tone="danger" onClick={() => setRemoving(a)}>
                  <TrashIcon />
                </IconButton>
              </RowActions>
            </div>
            {!a.is_default && (
              <button
                onClick={() => makeDefault.mutate(a.id)}
                disabled={makeDefault.isPending}
                className="mt-2 text-sm text-brand hover:underline disabled:opacity-50"
              >
                Set as default
              </button>
            )}
          </div>
        ))}
        {addresses.length === 0 && <p className="text-sm text-gray-400">No addresses yet.</p>}
      </div>

      <ConfirmDelete
        open={removing !== null}
        title="Remove address?"
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
      >
        {removing && (
          <p>
            <b className="text-gray-800">{removing.label || removing.name}</b> — {removing.line1}, {removing.city} — will be
            removed from your addresses.
          </p>
        )}
      </ConfirmDelete>

      <Modal open={formOpen} title={editing ? "Edit address" : "Add address"} onClose={() => setFormOpen(false)}>
        <AddressForm
          initial={editing}
          onCancel={() => setFormOpen(false)}
          onSubmit={async (vals) => {
            if (editing) {
              // "Default" isn't a field the edit takes — it has its own call (ACCT-05); the PATCH
              // used to drop it silently.
              const { is_default, ...fields } = vals;
              await updateAddress(editing.id, fields);
              if (is_default && !editing.is_default) await setDefaultAddress(editing.id);
            } else {
              await addAddress(vals, addKey.keyFor(vals));
              addKey.reset();
            }
            setFormOpen(false);
            invalidate();
          }}
        />
      </Modal>
    </div>
  );
}

function AddressForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: Address | null;
  onSubmit: (v: AddressInput) => Promise<void>;
  onCancel: () => void;
}) {
  const { busy, error, run } = useFormSubmit(onSubmit);
  const [form, setForm] = useState<AddressInput>({
    label: initial?.label ?? "",
    name: initial?.name ?? "",
    phone: initial?.phone ?? "+91",
    line1: initial?.line1 ?? "",
    line2: initial?.line2 ?? "",
    landmark: initial?.landmark ?? "",
    city: initial?.city ?? "",
    state_code: initial?.state_code ?? "",
    pin_code: initial?.pin_code ?? "",
    is_default: initial?.is_default ?? false,
  });
  const set = (p: Partial<AddressInput>) => setForm({ ...form, ...p });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValidPinCode(form.pin_code)) {
          toast.error("PIN code must be exactly 6 digits.");
          return;
        }
        void run(form);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Label"><input value={form.label ?? ""} onChange={(e) => set({ label: e.target.value })} className="w-full rounded-lg border px-3 py-1.5" /></Field>
        <Field label="Recipient name"><input value={form.name} onChange={(e) => set({ name: e.target.value })} required className="w-full rounded-lg border px-3 py-1.5" /></Field>
        <Field label="Phone"><PhoneInput value={form.phone} onChange={(v) => set({ phone: v })} required /></Field>
        <Field label="Line 1">
          <textarea value={form.line1} onChange={(e) => set({ line1: e.target.value })} required className="w-full rounded-lg border px-3 py-1.5" />
        </Field>
        <Field label="Line 2">
          <textarea value={form.line2 ?? ""} onChange={(e) => set({ line2: e.target.value })} className="w-full rounded-lg border px-3 py-1.5" />
        </Field>
        <Field label="Landmark"><input value={form.landmark ?? ""} onChange={(e) => set({ landmark: e.target.value })} className="w-full rounded-lg border px-3 py-1.5" /></Field>
        <Field label="City"><input value={form.city} onChange={(e) => set({ city: e.target.value })} required className="w-full rounded-lg border px-3 py-1.5" /></Field>
        <Field label="State">
          <StateSelect value={form.state_code} onChange={(code) => set({ state_code: code })} className="w-full" />
        </Field>
        <Field label="PIN code"><input
          value={form.pin_code}
          onChange={(e) => set({ pin_code: pinDigits(e.target.value) })}
          required
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={6}
          pattern="[1-9][0-9]{5}"
          title="Enter a 6-digit PIN code."
          placeholder="6-digit PIN"
          className="w-full rounded-lg border px-3 py-1.5"
        /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.is_default}
          // The default changes by choosing another address, not by unticking this one.
          disabled={!!initial?.is_default}
          onChange={(e) => set({ is_default: e.target.checked })}
        />
        {initial?.is_default ? "Default address — choose another address to change it" : "Set as default address"}
      </label>
      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button disabled={busy} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          {busy ? "Saving…" : initial ? "Save" : "Add"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-gray-500">{label}</span>
      {children}
    </label>
  );
}
